import { IMicrositeRecipientForList, IContact } from "@nkr/models/index"
import { Button, FormControl, Modal, Popover, Sizes } from "react-bootstrap";
import { ChangeEvent, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MicrositeSearchContext } from "./context";
import { ViewWrapper } from '@nkr/views/donors/watr/styles';
import CoachSelector from "./coach-selector";

interface IProps {
    recipient: IMicrositeRecipientForList
    isAdmin: boolean,
    selectorSize?: Sizes
}

function resolveCoachEmail(recipient: IMicrositeRecipientForList, coachList: IContact[] | undefined): string | null {
    const fromRow = recipient.coachContact?.email?.trim();
    if (fromRow) return fromRow;
    const fromList = coachList?.find(f => f.id === recipient.coachContactId)?.email?.trim();
    return fromList || null;
}

function CoachEmailPopover({ recipientId, email }: { recipientId: number, email: string }) {
    return (
        <Popover id={`coach-email-popover-${recipientId}`} title="Coach email" style={{ maxWidth: 320, position: 'relative', top: 0, left: 0, margin: 0 }}>
            <div style={{ wordBreak: 'break-word' }}>{email}</div>
        </Popover>
    );
}

function CoachEmailHoverPortal({
    recipientId,
    email,
    children,
}: {
    recipientId: number,
    email: string,
    children: ReactNode,
}) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHideTimer = () => {
        if (hideTimerRef.current != null) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const updatePosition = useCallback(() => {
        const el = wrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const maxW = 320;
        const margin = 8;
        const preferRight = r.right + margin + maxW <= window.innerWidth - margin;
        const left = preferRight
            ? r.right + margin
            : Math.max(margin, r.left - maxW - margin);
        const top = Math.max(margin, r.top);
        setPos({ top, left });
    }, []);

    const open = () => {
        clearHideTimer();
        updatePosition();
        setVisible(true);
    };

    const scheduleHide = () => {
        clearHideTimer();
        hideTimerRef.current = setTimeout(() => setVisible(false), 120);
    };

    useEffect(() => {
        if (!visible) return;
        const hide = () => setVisible(false);
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [visible]);

    const portal =
        visible &&
        createPortal(
            <div
                style={{
                    position: 'fixed',
                    top: pos.top,
                    left: pos.left,
                    zIndex: 1060,
                    pointerEvents: 'auto',
                }}
                onMouseEnter={clearHideTimer}
                onMouseLeave={scheduleHide}
            >
                <CoachEmailPopover recipientId={recipientId} email={email} />
            </div>,
            document.body,
        );

    return (
        <>
            <div
                ref={wrapRef}
                className="coach-email-popover-trigger"
                onMouseEnter={() => {
                    clearHideTimer();
                    open();
                }}
                onMouseLeave={scheduleHide}
            >
                {children}
            </div>
            {portal}
        </>
    );
}

const CoachCell = ({ recipient, isAdmin, selectorSize }: IProps) => {
    const context = useContext(MicrositeSearchContext);

    const coachEmail = useMemo(
        () => resolveCoachEmail(recipient, context.coachList),
        [recipient, context.coachList],
    );
    const showCoachEmailPopover = recipient.coachContactId > 0 && coachEmail != null && coachEmail.length > 0;

    const [show, setShow] = useState<boolean>(false);

    const newCoachId = useRef<number | null>(null)

    const handleChange = (e: ChangeEvent<FormControl & HTMLSelectElement & HTMLInputElement>) => {
        newCoachId.current = parseInt(e.target.value);
        setShow(true);
    }

    const handleCancel = () => {
        setShow(false);
    }

    const handleConfirm = () => {
        context.onCoachChange(recipient.id, newCoachId.current);
        setShow(false);
        newCoachId.current = null;
    }

    const oldCoach = context.coachList.find(f => f.id === recipient.coachContactId);
    const newCoach = context.coachList.find(f => f.id === newCoachId.current);

    const coachSelectorTitle = recipient?.isCoachingDesired ? `` : `Opted Out of Coaching`;

    const coachSelector = (
        <CoachSelector
            value={recipient.coachContactId}
            defaultText={` - None - `}
            title={showCoachEmailPopover ? undefined : coachSelectorTitle}
            disabled={!(recipient.coachContactId > 0) && recipient?.isCoachingDesired === false && recipient?.isCoachOptIn === false}
            allowNone={isAdmin}
            bsSize={selectorSize}
            onChange={handleChange}
        />
    );

    if (!isAdmin) {
        const nameCell = <div className="coach-email-popover-label">{recipient?.coachContactName}</div>;
        if (showCoachEmailPopover) {
            return (
                <CoachEmailHoverPortal recipientId={recipient.id} email={coachEmail}>
                    {nameCell}
                </CoachEmailHoverPortal>
            );
        }
        return nameCell;
    } else {
        return (
            <>
                {showCoachEmailPopover ? (
                    <CoachEmailHoverPortal recipientId={recipient.id} email={coachEmail}>
                        {coachSelector}
                    </CoachEmailHoverPortal>
                ) : (
                    coachSelector
                )}

                <Modal backdrop='static' show={show} onHide={handleCancel}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm New Coach</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ViewWrapper>
                            {recipient && recipient.center != null && <span>For Recipient: <strong>{recipient.fullName} @ {recipient.center.alias} (Id: {recipient.id})</strong><br /><br /></span>}
                            {recipient && recipient.center == null && <span>For Recipient: <strong>{recipient.fullName} (Id: {recipient.id})</strong><br /><br /></span>}
                        </ViewWrapper>

                        {oldCoach &&
                            <ViewWrapper>
                                From: <strong>{oldCoach.fullName} ({oldCoach.currentCaseLoad}/{oldCoach.maxCaseLoad})</strong>
                            </ViewWrapper>
                        }

                        {!oldCoach &&
                            <ViewWrapper>
                                From: <strong>[none selected]</strong>
                            </ViewWrapper>
                        }

                        {newCoach &&
                            <ViewWrapper>
                                To Coach: <strong>{newCoach.fullName} ({newCoach.currentCaseLoad}/{newCoach.maxCaseLoad})</strong>
                            </ViewWrapper>
                        }

                        {!newCoach &&
                            <ViewWrapper>
                                To Coach: <strong>Not Assigned</strong>
                            </ViewWrapper>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        <Button bsStyle='primary' onClick={handleConfirm}>Confirm</Button>
                        <Button onClick={handleCancel}>Cancel</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default CoachCell;
